<template>
  <div class="login-container">
    <!-- 多租戶登入表單：需要輸入租戶 ID、帳號和密碼 -->
    <el-form
      ref="loginForm"
      :model="loginForm"
      :rules="loginRules"
      class="login-form"
      autocomplete="on"
      label-position="left">
      <div class="title-container">
        <h3 class="title">
          <img src="@/assets/logo/logo.png" alt="Logo" class="logo-icon" />
          Ailisi 電子簽名系統
        </h3>
      </div>

      <!-- 租戶 ID 輸入欄位 -->
      <el-form-item prop="tenantId">
        <span class="svg-container">
          <i class="el-icon-office-building" />
        </span>
        <el-input
          ref="tenantId"
          v-model="loginForm.tenantId"
          placeholder="請輸入診所代碼"
          name="tenantId"
          type="text"
          tabindex="1"
          autocomplete="on" />
      </el-form-item>

      <!-- 帳號輸入欄位 -->
      <el-form-item prop="username">
        <span class="svg-container">
          <i class="el-icon-user" />
        </span>
        <el-input
          ref="username"
          v-model="loginForm.username"
          placeholder="請輸入帳號"
          name="username"
          type="text"
          tabindex="2"
          autocomplete="on" />
      </el-form-item>

      <!-- 密碼輸入欄位（支援顯示/隱藏切換和 Caps Lock 提示） -->
      <el-tooltip v-model="capsTooltip" content="Caps lock is On" placement="right" manual>
        <el-form-item prop="password">
          <span class="svg-container">
            <i class="el-icon-lock" />
          </span>
          <el-input
            :key="passwordType"
            ref="password"
            v-model="loginForm.password"
            :type="passwordType"
            placeholder="請輸入密碼"
            name="password"
            tabindex="3"
            autocomplete="on"
            @keyup.native="checkCapslock"
            @blur="capsTooltip = false"
            @keyup.enter.native="handleLogin" />
          <span class="show-pwd" @click="showPwd">
            <svg-icon :icon-class="passwordType === 'password' ? 'eye' : 'eye-open'" />
          </span>
        </el-form-item>
      </el-tooltip>

      <!-- 登入按鈕 -->
      <el-button
        :loading="loading"
        type="primary"
        style="width: 100%; margin-bottom: 30px"
        @click.native.prevent="handleLogin"
        >登入</el-button
      >
    </el-form>
  </div>
</template>

<script>
export default {
  name: 'Login',
  data() {
    return {
      // 登入表單資料
      loginForm: {
        tenantId: '',
        username: '',
        password: ''
      },
      // 表單驗證規則
      loginRules: {
        tenantId: [{ required: true, message: '請輸入診所代碼', trigger: 'blur' }],
        username: [{ required: true, message: '請輸入帳號', trigger: 'blur' }],
        password: [{ required: true, message: '請輸入密碼', trigger: 'blur' }]
      },
      passwordType: 'password', // 密碼輸入框類型（password 或 text）
      capsTooltip: false, // Caps Lock 提示顯示狀態
      loading: false, // 登入按鈕載入狀態
      redirect: undefined, // 登入成功後的跳轉路徑
      otherQuery: {} // 其他查詢參數（用於登入後跳轉時保留）
    }
  },
  watch: {
    // 監聽路由變化，處理登入後的跳轉
    $route: {
      handler: function (route) {
        const query = route.query
        if (query) {
          this.redirect = query.redirect
          this.otherQuery = this.getOtherQuery(query)
        }
      },
      immediate: true
    }
  },
  mounted() {
    if (sessionStorage.getItem('showReLoginMessage')) {
      this.$message.warning('您的登入狀態已失效，請重新登入')
      sessionStorage.removeItem('showReLoginMessage')
    }
  },
  methods: {
    /**
     * 檢查 Caps Lock 是否開啟
     * @param {KeyboardEvent} e - 鍵盤事件
     */
    checkCapslock(e) {
      const { key } = e
      this.capsTooltip = key && key.length === 1 && key >= 'A' && key <= 'Z'
    },
    /**
     * 切換密碼顯示/隱藏
     */
    showPwd() {
      if (this.passwordType === 'password') {
        this.passwordType = ''
      } else {
        this.passwordType = 'password'
      }
      this.$nextTick(() => {
        this.$refs.password.focus()
      })
    },
    /**
     * 處理登入邏輯
     * 驗證表單後，調用 Store 的 login action 進行登入
     * Token 格式：Basic <base64(tenantId:username:password)>
     */
    handleLogin() {
      this.$refs.loginForm.validate((valid) => {
        if (valid) {
          this.loading = true
          this.$store
            .dispatch('user/login', this.loginForm)
            .then(() => {
              // 登入成功，跳轉到目標頁面
              this.$router.push({ path: this.redirect || '/', query: this.otherQuery })
              this.loading = false
            })
            .catch(() => {
              // 登入失敗，錯誤訊息由攔截器處理
              this.loading = false
            })
        } else {
          // 表單驗證失敗，不執行登入
          return false
        }
      })
    },
    /**
     * 從查詢參數中過濾出 redirect 以外的參數
     * @param {Object} query - 路由查詢參數
     * @returns {Object} 過濾後的查詢參數
     */
    getOtherQuery(query) {
      return Object.keys(query).reduce((acc, cur) => {
        if (cur !== 'redirect') {
          acc[cur] = query[cur]
        }
        return acc
      }, {})
    }
  }
}
</script>

<style lang="scss">
/**
 * 修復 input 背景不協調和游標變色問題
 * 參考：https://github.com/vue-element-admin/pull/927
 */

$bg: #283443;
$light_gray: #fff;
$cursor: #fff;

@supports (-webkit-mask: none) and (not (cater-color: $cursor)) {
  .login-container .el-input input {
    color: $cursor;
  }
}

/* 重置 Element UI 預設樣式 */
.login-container {
  .el-input {
    display: inline-block !important;
    height: 47px !important;
    width: 85% !important;

    // 針對 Element UI 的內部結構設定樣式
    .el-input__inner {
      height: 47px !important;
      line-height: 47px !important; // 設定 line-height 等於 height，確保文字垂直置中
      padding: 0 5px 0 15px !important; // 移除上下 padding，改用 line-height 控制垂直位置
      box-sizing: border-box !important; // 確保 padding 包含在 height 內
      vertical-align: middle !important;
    }

    input {
      background: transparent !important;
      border: 0px !important;
      -webkit-appearance: none !important;
      border-radius: 0px !important;
      height: 47px !important;
      line-height: 47px !important; // 設定 line-height 等於 height，確保文字垂直置中
      padding: 0 5px 0 15px !important; // 移除上下 padding，改用 line-height 控制垂直位置
      color: $light_gray !important;
      caret-color: $cursor !important;
      box-sizing: border-box !important; // 確保 padding 包含在 height 內
      vertical-align: middle !important;

      &:-webkit-autofill {
        box-shadow: 0 0 0px 1000px $bg inset !important;
        -webkit-text-fill-color: $cursor !important;
      }
    }
  }

  .el-form-item {
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    background: rgba(0, 0, 0, 0.1) !important;
    border-radius: 5px !important;
    color: #454545 !important;
  }

  .el-button {
    height: 47px !important;
    line-height: 47px !important;
    padding: 0 !important;
    font-size: 14px !important;
  }
}
</style>

<style lang="scss" scoped>
$bg: #2d3a4b;
$dark_gray: #889aa4;
$light_gray: #eee;

.login-container {
  min-height: 100%;
  width: 100%;
  background-color: $bg;
  overflow: hidden;

  .login-form {
    position: relative;
    width: 520px;
    max-width: 100%;
    padding: 160px 35px 0;
    margin: 0 auto;
    overflow: hidden;
  }

  .tips {
    font-size: 14px;
    color: #fff;
    margin-bottom: 10px;

    span {
      &:first-of-type {
        margin-right: 16px;
      }
    }
  }

  .svg-container {
    padding: 6px 5px 6px 15px;
    color: $dark_gray;
    vertical-align: middle;
    width: 30px;
    display: inline-block;
  }

  .title-container {
    position: relative;

    .title {
      font-size: 26px;
      color: $light_gray;
      margin: 0px auto 40px auto;
      text-align: center;
      font-weight: bold;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;

      .logo-icon {
        width: 32px;
        height: 32px;
        object-fit: contain;
      }
    }
  }

  .show-pwd {
    position: absolute;
    right: 10px;
    top: 7px;
    font-size: 16px;
    color: $dark_gray;
    cursor: pointer;
    user-select: none;
  }
}
</style>
